import { useEffect } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import {
  Languages,
  Regions,
  Site,
  SiteCreateAttributes,
  SiteUpdateAttributes,
} from "~/api/Api";
import { MappedValidationError } from "~/utils/errors/validationErrors";

import NotificationBlock from "../../notificationBlock/NotificationBlock";
import Button from "../../ui/button/Button";
import { tabs } from "./NewSite";
import styles from "./NewSiteCompanyDetails.module.scss";
import { SiteDetailsForm } from "./siteDetails/SiteDetailsForm";

type NewSiteCompanyDetailsProps = {
  form: ReturnType<typeof useForm<SiteCreateAttributes>>;
  setActiveTab: (tab: string) => void;
  updateSite: (data: SiteUpdateAttributes) => void;
  serverErrors: MappedValidationError[];
  site: Site | undefined | null;
  isPendingUpdateSite: boolean;
  languages: Languages;
  regions: Regions;
  isFetchingCompanyDetails: boolean;
};
const NewSiteCompanyDetails = ({
  setActiveTab,
  updateSite,
  serverErrors,
  site,
  isPendingUpdateSite,
  regions,
  languages,
}: NewSiteCompanyDetailsProps) => {
  const navigate = useNavigate();

  const form = useForm<SiteCreateAttributes>();
  const {
    watch,
    getValues,
    reset,
    formState: { isDirty },
  } = form;
  watch("primary_country");
  watch("primary_language");
  watch("site_type");

  // Initialize the form once the site is available. The parent polls the site
  // for keyword suggestions, so depending on the whole site object (or using a
  // reactive `values`) would re-sync the form on every poll and discard the
  // user's edits — key the reset on the stable site id instead.
  useEffect(() => {
    if (!site) return;

    reset({
      primary_country: site.primary_country || "",
      primary_language: site.primary_language || "",
      site_type: site.site_type || "AI / SaaS / Startup",
      url: "",
    });
  }, [site?.id, reset]);

  const onSubmit: SubmitHandler<SiteCreateAttributes> = (data) => {
    if (!isDirty) {
      setActiveTab(tabs[3]);
      navigate(tabs[3]);

      return;
    }
    updateSite(data);
    reset(data);
  };
  const isDisabled =
    !getValues().primary_country ||
    !getValues().primary_language ||
    isPendingUpdateSite;

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <h2 className={styles.title}>Add Details</h2>
        {serverErrors.length > 0 && (
          <NotificationBlock type="danger">
            <>
              {serverErrors.map((e) => (
                <p key={e.code}>
                  {e.message} (Code: {e.code})
                </p>
              ))}
            </>
          </NotificationBlock>
        )}
        <p className={styles.description}>
          Some basic info about your business and traffic will help us tailor
          recommendations to your needs.
        </p>
        <SiteDetailsForm
          languages={languages}
          regions={regions}
          onSubmit={onSubmit}
          form={form}
        />
      </div>
      <div className={styles.footer}>
        <Button
          outlined
          onClick={() => {
            setActiveTab(tabs[1]);
            navigate(tabs[1]);
          }}
        >
          Back
        </Button>
        <Button
          type="submit"
          formId="newSiteDetailsForm"
          disabled={isDisabled}
          withSpinner={isPendingUpdateSite}
        >
          Continue
        </Button>
      </div>
    </div>
  );
};
export default NewSiteCompanyDetails;
